import posthog from "posthog-js";
import { useCallback } from "react";

import { JobDetails } from "../utils/helpers";

interface JobAnalyticsData {
	jobId?: string;
	jobTitle?: string;
	companyName?: string;
	companyId?: string;
}

/**
 * Hook for tracking job page analytics events
 * Events help answer: "What actually convinces people to apply?"
 */
export function useJobPageAnalytics(jobDetails: JobDetails) {
	const analyticsData: JobAnalyticsData = {
		jobId: jobDetails?._id,
		jobTitle: jobDetails?.jobTitle,
		companyName: jobDetails?.company?.company_name,
		companyId: jobDetails?.company?._id,
	};

	// Role Explainer events
	const trackRoleExplainerOpened = useCallback(() => {
		posthog.capture("role_explainer_opened", analyticsData);
	}, [analyticsData]);

	const trackRoleExplainerViewed = useCallback(() => {
		posthog.capture("role_explainer_viewed", analyticsData);
	}, [analyticsData]);

	// FitCheck events
	const trackFitCheckStarted = useCallback(() => {
		posthog.capture("fitcheck_started", analyticsData);
	}, [analyticsData]);

	const trackFitCheckCompleted = useCallback(
		(matchLevel: "high" | "partial" | "low") => {
			posthog.capture("fitcheck_completed", {
				...analyticsData,
				matchLevel,
			});
		},
		[analyticsData],
	);

	// Apply events - with context about what preceded the apply
	const trackApplyClicked = useCallback(
		(context: { afterExplainer?: boolean; afterFitCheck?: boolean; directApply?: boolean }) => {
			const eventName = context.afterExplainer
				? "apply_clicked_after_explainer"
				: context.afterFitCheck
					? "apply_clicked_after_fitcheck"
					: "apply_clicked_direct";

			posthog.capture(eventName, {
				...analyticsData,
				...context,
			});
		},
		[analyticsData],
	);

	// Job interaction events
	const trackJobSaved = useCallback(() => {
		posthog.capture("job_saved", analyticsData);
	}, [analyticsData]);

	const trackJobShared = useCallback(
		(platform?: string) => {
			posthog.capture("job_shared", {
				...analyticsData,
				platform,
			});
		},
		[analyticsData],
	);

	const trackJobMarkedIrrelevant = useCallback(() => {
		posthog.capture("job_marked_irrelevant", analyticsData);
	}, [analyticsData]);

	// Job Description events
	const trackJobDescriptionExpanded = useCallback(() => {
		posthog.capture("job_description_expanded", analyticsData);
	}, [analyticsData]);

	// Job List events
	const trackExploreClicked = useCallback(() => {
		posthog.capture("job_list_explore_clicked", analyticsData);
	}, [analyticsData]);

	return {
		// Role Explainer
		trackRoleExplainerOpened,
		trackRoleExplainerViewed,
		// FitCheck
		trackFitCheckStarted,
		trackFitCheckCompleted,
		// Apply
		trackApplyClicked,
		// Interactions
		trackJobSaved,
		trackJobShared,
		trackJobMarkedIrrelevant,
		trackJobDescriptionExpanded,
		// Job List
		trackExploreClicked,
	};
}
